/** Mounts the GoDaddy Inbox chat widget using the reamaze-loader script. */

import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'reamaze-loader-js';

declare global {
  interface Window {
    _support?: Record<string, unknown>;
  }
}

function getLoaderUrl(): string {
  const hostname = window.location.hostname;
  if (hostname.endsWith('.dev-godaddy.com') || hostname.endsWith('.dev-airoapp.ai')) {
    return 'https://www.dev-reamaze.com/assets/reamaze-loader.js';
  }
  if (hostname.endsWith('.test-godaddy.com') || hostname.endsWith('.test-airoapp.ai')) {
    return 'https://www.test-reamaze.com/assets/reamaze-loader.js';
  }
  return 'https://cdn.reamaze.com/assets/reamaze-loader.js';
}

export default function InboxChatWidget() {
  const injected = useRef(false);

  useEffect(() => {
    const loadWidget = () => {
      if (injected.current || document.getElementById(SCRIPT_ID)) return;
      injected.current = true;

      // Configure the widget immediately before its optional script loads.
      window._support = window._support || { ui: {}, user: {} };
      window._support['account'] = '72e6b8eb-b34b-48d0-9da6-883c6c9ad2de';
      window._support['ui'] = {
        contactMode: 'mixed',
        enableKb: 'true',
        styles: {
          widgetColor: '#870D13',
          gradient: true,
        },
        shoutboxFacesMode: '',
        widget: {
          icon: 'webChat',
          slug: 'sand-and-sun-travels',
          label: {
            text: 'Let us know if you have any questions!',
            mode: 'notification',
            delay: 3,
            duration: 30,
            sound: true,
          },
          position: 'bottom-right',
        },
      };
      window._support['apps'] = {
        recentConversations: {},
        faq: { enabled: true },
      };

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = getLoaderUrl();
      document.head.appendChild(script);
    };

    // A third-party chat bundle is not required for the first view. Load it when
    // a visitor begins interacting, keeping it out of the mobile LCP request chain.
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
    const activate = () => {
      events.forEach((event) => window.removeEventListener(event, activate));
      loadWidget();
    };
    events.forEach((event) => window.addEventListener(event, activate, { passive: true, once: true }));

    return () => events.forEach((event) => window.removeEventListener(event, activate));
  }, []);

  return null;
}
